// ============================================================================
// GsFeedbackButton — bulle d'acces rapide aux Commentaires (frere de GsNotificationBell)
// ============================================================================
// MASTER-DS-CHROME-FEEDBACK 2026-06-15 : la POC avait une bulle Commentaires a cote
// de la cloche. Bouton-icone PUR — PAS de badge (decision David v1 : la cloche porte
// deja FEEDBACK_CREATED, un 2e badge double-compterait) ; donc AUCUN polling.
//
// CAPABILITY-AWARE (mirror GsNotificationBell) : sonde /api/feedback/unread-count UNE
// fois ; si 404 (route feedback non montee) / 503 (migration en attente) / 401 / 403,
// la bulle SE MASQUE — aucun controle mort dans une app non branchee, aucun 500 surface.
//
// Pas de t() dans @gs/ui (pattern GsNotificationBellLabels) : libelles via props, defauts FR.
import { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { api } from '@gs/api-client';

export interface GsFeedbackButtonLabels {
  /** aria-label du bouton (defaut « Commentaires »). */
  button?: string;
}

export interface GsFeedbackButtonProps {
  /** Desactive la sonde tant que l'app n'est pas prete (ex: !user). */
  enabled?: boolean;
  /** Endpoint de sonde capability (defaut /api/feedback/unread-count — module @gs/feedback). */
  capabilityEndpoint?: string;
  /** Clic = naviguer vers la vue Commentaires de l'app. */
  onNavigate?: () => void;
  labels?: GsFeedbackButtonLabels;
}

function isCapabilityError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 404 || status === 503 || status === 401 || status === 403;
}

export function GsFeedbackButton({
  enabled = true,
  capabilityEndpoint = '/api/feedback/unread-count',
  onNavigate,
  labels,
}: GsFeedbackButtonProps) {
  const [capable, setCapable] = useState<boolean | null>(null); // null = pas encore sonde

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    // setState UNIQUEMENT dans les callbacks (reponse reseau = systeme externe) — pattern
    // GsNotificationBell (un setState synchrone dans le corps de l'effet est rejete par le
    // React Compiler). `.then(onF, onR)` (onRejected DIRECT sur la sonde) plutot que
    // `.then().catch()` : evite toute promesse intermediaire transitoirement rejetee.
    api.get(capabilityEndpoint).then(
      () => { if (active) setCapable(true); },
      (err: unknown) => { if (active && isCapabilityError(err)) setCapable(false); },
    );
    return () => { active = false; };
  }, [enabled, capabilityEndpoint]);

  if (!enabled || capable !== true) return null;

  return (
    <button
      type="button"
      onClick={() => onNavigate?.()}
      aria-label={labels?.button ?? 'Commentaires'}
      className="touch-manipulation rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    >
      <MessageSquare size={15} aria-hidden />
    </button>
  );
}
